import { useMemo, useState } from "react";
import { ShoppingCart } from "lucide-react";
import {
  CopyHex, EmptyState, ErrorBanner, FilterChips, Investor, ORDER_STATUS, RowsSkeleton, SearchInput,
  SectionHeader, StatusBadge, fmtDateTime, fmtNum, fmtUsd,
} from "./bcShared";

const GROUPS = {
  completed: ["COMPLETED"],
  progress: ["AWAITING_PAYMENT", "PAID", "TRANSFERRING"],
  failed: ["PAYMENT_FAILED", "TRANSFER_FAILED"],
};

const COLS = "md:grid-cols-[1.1fr,1.4fr,1.3fr,0.5fr,0.8fr,1fr,1fr]";

export default function BlockchainOrders({ orders, loading, error, onRetry }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return orders.filter((o) => {
      if (filter !== "all" && !GROUPS[filter].includes(o.status)) return false;
      if (!needle) return true;
      return [o.id, o.collectionName, o.symbol, o.userWalletAddress, o.transferTxHash, o.investor?.name, o.investor?.email]
        .some((v) => v?.toLowerCase().includes(needle));
    });
  }, [orders, filter, q]);

  const count = (g) => orders.filter((o) => GROUPS[g].includes(o.status)).length;
  const totals = rows.reduce(
    (t, o) => (o.status === "COMPLETED" ? { qty: t.qty + o.quantity, cents: t.cents + (o.fees?.totalCents || 0) } : t),
    { qty: 0, cents: 0 },
  );

  return (
    <div className="zc-card overflow-hidden zc-stack-table" data-testid="bc-orders">
      <SectionHeader title="Token orders" count={orders.length}>
        <div className="flex flex-wrap items-center gap-3">
          <FilterChips
            value={filter}
            onChange={setFilter}
            options={[
              { id: "all", label: "All", count: orders.length },
              { id: "completed", label: "Completed", count: count("completed") },
              { id: "progress", label: "In progress", count: count("progress") },
              { id: "failed", label: "Failed", count: count("failed") },
            ]}
          />
          <SearchInput value={q} onChange={setQ} placeholder="Search investor, collection, order or tx…" />
        </div>
      </SectionHeader>

      {error && <ErrorBanner message={error} onRetry={onRetry} />}

      <div className={`hidden md:grid ${COLS} gap-4 zc-stack-head px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold`}>
        <div>Order</div><div>Investor</div><div>Collection</div><div>Qty</div><div>Total</div><div>Status</div><div>Transfer tx</div>
      </div>

      {loading && orders.length === 0 && <RowsSkeleton />}
      {!loading && !error && rows.length === 0 && (
        <EmptyState icon={ShoppingCart} title={orders.length ? "No orders match" : "No orders yet"} hint={orders.length ? "Try a different filter or search." : "Orders appear here once investors buy tokens."} />
      )}

      {rows.map((o) => {
        const st = ORDER_STATUS[o.status] || { label: o.status, tone: "neutral" };
        return (
          <div key={o.id} className={`zc-stack-row md:grid ${COLS} md:items-center gap-4 px-4 sm:px-6 py-3.5 border-b border-[var(--zc-border)] last:border-0 text-sm`}>
            <div data-label="Order" className="min-w-0">
              <CopyHex value={o.id} head={8} tail={4} label="Order ID" />
              <div className="text-2xs text-[var(--zc-text-3)] mt-0.5">{fmtDateTime(o.createdAt)}</div>
            </div>
            <div data-label="Investor" className="min-w-0"><Investor investor={o.investor} fallbackAddress={o.userWalletAddress} /></div>
            <div data-label="Collection" className="min-w-0">
              <div className="truncate">{o.collectionName || "—"}</div>
              <div className="text-2xs text-[var(--zc-text-3)] mt-0.5"><span className="font-mono">{o.symbol}</span> · #{o.tokenId}</div>
            </div>
            <div data-label="Qty" className="tabular-nums">{fmtNum(o.quantity)}</div>
            <div data-label="Total" className="font-semibold tabular-nums">{fmtUsd(o.fees?.totalCents)}</div>
            <div data-label="Status" className="min-w-0">
              <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
              {o.errorMessage && <div className="text-2xs text-[var(--zc-danger)] mt-1 line-clamp-2" title={o.errorMessage}>{o.errorMessage}</div>}
            </div>
            <div data-label="Transfer tx"><CopyHex value={o.transferTxHash} label="Transaction hash" /></div>
          </div>
        );
      })}

      {rows.length > 0 && (
        <div className="px-4 sm:px-6 py-3 bg-[var(--zc-bg-3)] text-xs text-[var(--zc-text-2)] flex flex-wrap justify-between gap-2 tabular-nums">
          <span>Showing {fmtNum(rows.length)} of {fmtNum(orders.length)}</span>
          <span>Completed in view: <b className="text-[var(--zc-text)]">{fmtNum(totals.qty)} tokens · {fmtUsd(totals.cents)}</b></span>
        </div>
      )}
    </div>
  );
}
